import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadAsset } from '@/entities/asset';
import { notificationService } from '@/lib/services/notification.service';
import { uploadSectionFiles } from '../../../lib/uploadSectionFiles';
import { FileField, type FileFieldValue } from './FileField';
import { useAttachmentResolver } from './useAttachmentResolver';
import type { FileFieldConfig } from './validateFiles';

interface FileFieldContainerProps {
  value: FileFieldValue | null;
  config: FileFieldConfig;
  disabled?: boolean;
  onChange: (next: FileFieldValue) => void;
}

/**
 * Connects the pure `FileField` to the asset service: the widget decides which picked files are
 * allowed, this owns the upload they need before the section has anything to persist.
 *
 * Only the asset ids are kept. `useUploadAsset` also returns a `url`, but it is signed and
 * expires, so a brief that stored one would render broken links a day later.
 */
export function FileFieldContainer({ value, config, disabled, onChange }: FileFieldContainerProps) {
  const { t } = useTranslation('projects');
  const { mutateAsync } = useUploadAsset();
  const [isUploading, setIsUploading] = useState(false);
  const resolveAsset = useAttachmentResolver(value?.assetIds ?? []);

  const runUpload = async (files: File[]) => {
    setIsUploading(true);
    const result = await uploadSectionFiles(value?.assetIds ?? [], files, (file) =>
      mutateAsync(file),
    );
    setIsUploading(false);

    if (result.status === 'failed') {
      notificationService.showError(
        t('projectBriefDetail.briefSectionField.fileField.uploadFailed'),
      );
      return;
    }

    onChange({ assetIds: result.assetIds });
  };

  return (
    <FileField
      value={value}
      config={config}
      disabled={disabled}
      isUploading={isUploading}
      onChange={onChange}
      onFilesSelected={(files) => void runUpload(files)}
      resolveAsset={resolveAsset}
    />
  );
}
